import type { ReactElement } from "react"
import styled from "styled-components"
import { useEntitlements } from "../api/useEntitlements"
import { manageAccessUrl, preferredManageTab } from "../manageAccess"

/**
 * The page-level Manage Access affordance, matching MLC's header button
 * (`containers/DigitalMediaRQ.js:278`).
 *
 * Sharing seats stays in myLifeway — this is a link out, not a port of the page.
 * It needs nothing from us to work: `ManageAccessContainer` fetches `/licenses`
 * on mount and redirects a missing media type to its default, so even the tab is
 * optional (FINDINGS.md gap 25).
 *
 * Rendered unconditionally, as MLC renders it. Suppressing it for an account with
 * nothing to manage would need licence data on every row, which the deployed
 * graph has none of; with the POC facade the tab preference is the one thing the
 * commerce rows can improve.
 */
const Link = styled.a`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  border: 1px solid ${({ theme }) => theme.color.borderStrong};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => theme.color.surfaceSubtle};
  }
`

export const ManageAccessButton = (): ReactElement => {
  const { items } = useEntitlements()
  const tab = preferredManageTab(
    items.map(item => ({ category: item.category, isManageable: Boolean(item.window.manageHref) })),
  )

  return (
    <Link
      href={manageAccessUrl(tab)}
      target="_blank"
      rel="noopener noreferrer"
      data-qa-hook="digital-library-manage-access"
    >
      Manage Access
    </Link>
  )
}
